"use client";

import React, { useMemo } from "react";

export interface SearchResult {
  objectID: string;
  title?: string;
  name?: string;
  description?: string;
  type: string;
  url: string;
  iconType: string;
  subtitle?: string;
  tags?: string[];
  highlightedTitle?: string | null;
  highlightedContent?: string | null;
  status?: string;
  priority?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SearchResultGroupProps {
  results: SearchResult[];
  getResultIcon: (type: string) => React.ReactNode;
  renderHighlightedText: (text: string) => React.ReactNode;
  onResultClick: (result: SearchResult) => void;
}

interface GroupedResults {
  [key: string]: SearchResult[];
}

const TYPE_LABELS: Record<string, string> = {
  ticket: "Tickets",
  contact: "Contacts",
  time_entry: "Time Entries",
  note: "Notes",
  task: "Tasks",
  message: "Messages",
  email: "Emails",
  template: "Templates",
  doc: "Documentation",
  proposal: "Proposals",
};

const TYPE_ORDER = ["ticket", "contact", "task", "time_entry", "message", "email", "note", "template", "doc", "proposal"];

export function SearchResultGroup({
  results,
  getResultIcon,
  renderHighlightedText,
  onResultClick,
}: SearchResultGroupProps) {
  // Group results by type
  const groupedResults = useMemo(() => {
    const grouped: GroupedResults = {};
    
    results.forEach(result => {
      if (!grouped[result.type]) {
        grouped[result.type] = [];
      }
      grouped[result.type].push(result);
    });

    return grouped;
  }, [results]);

  // Sort groups by TYPE_ORDER
  const sortedTypes = Object.keys(groupedResults).sort(
    (a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b)
  );

  return (
    <div className="space-y-4">
      {sortedTypes.map(type => {
        const typeResults = groupedResults[type];
        const label = TYPE_LABELS[type] || type.replace("_", " ");

        return (
          <div key={type} className="space-y-2">
            {/* Group header */}
            <div className="px-0 py-2 flex items-center justify-between border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-sm text-gray-900 capitalize">{label}</h3>
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-xs font-medium text-gray-700">
                  {typeResults.length}
                </span>
              </div>
            </div>

            {/* Group results */}
            <div className="divide-y divide-gray-100">
              {typeResults.map((result) => (
                <button type="button"
                  key={result.objectID}
                  onClick={() => onResultClick(result)}
                  className="w-full px-0 py-3 hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5 text-gray-400 group-hover:text-gray-600">
                      {getResultIcon(result.iconType)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 space-y-1">
                      {/* Title */}
                      <div className="text-sm font-medium text-gray-900 line-clamp-1 group-hover:text-blue-600">
                        {result.highlightedTitle ? 
                          renderHighlightedText(result.highlightedTitle) : 
                          result.title
                        }
                      </div>
                      
                      {/* Subtitle with metadata */}
                      {result.subtitle && (
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {result.subtitle}
                        </p>
                      )}
                      
                      {/* Content preview */}
                      {result.highlightedContent && (
                        <div className="text-xs text-gray-600 line-clamp-2 mt-1">
                          {renderHighlightedText(result.highlightedContent)}
                        </div>
                      )}
                      
                      {/* Tags */}
                      {result.tags && result.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {result.tags.slice(0, 2).map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                          {result.tags.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{result.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Status/Priority badge */}
                    {(result.status || result.priority) && (
                      <div className="flex-shrink-0 flex flex-col items-end space-y-1">
                        {result.status && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                            {result.status}
                          </span>
                        )}
                        {result.priority && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            result.priority === 'high' ? 'bg-red-100 text-red-800' :
                            result.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {result.priority}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
